import type { components } from "@sagot/api-client";
import { ZOHO_SYNC_API_NAME_PATTERN, ZOHO_SYNC_OBJECTS, type ZohoSyncErrorCode, type ZohoSyncObject } from "@sagot/constants";

type Schemas = components["schemas"];
export type ZohoSync = Schemas["ZohoSyncOut"];
export type ZohoSyncBody = Schemas["ZohoSyncIn"];
export type ZohoSyncRun = Schemas["ZohoSyncRunOut"];
export type SyncField = Schemas["SyncFieldOut"];
export type MappingRow = Schemas["MappingRowOut"];

const NAME_PATTERN = new RegExp(ZOHO_SYNC_API_NAME_PATTERN);
const HERE_COUNTS = ["created_here", "updated_here", "linked", "archived", "restored"] as const;
const THERE_COUNTS = ["created_there", "updated_there"] as const;
const FAILED_KEY = "failed";

/** The i18n key segment of a field (`contact.name` -> `contact_name`; key segments carry no dots). */
export function fieldKey(local: string): string {
  return local.replace(".", "_");
}

/** What the member typed per field (`contact.name` -> `Full_Name`); an empty value leaves the field out. */
export type MappingDraft = Readonly<Record<string, string>>;

export function draftOf(mapping: readonly MappingRow[]): MappingDraft {
  return Object.fromEntries(mapping.map((row) => [row.local, row.zoho]));
}

/** The standard mapping (every field that has a standard Zoho field), for "Reset". */
export function standardDraft(fields: readonly SyncField[]): MappingDraft {
  return Object.fromEntries(fields.filter((field) => field.default_zoho).map((field) => [field.local, field.default_zoho ?? ""]));
}

export function fieldsOf(fields: readonly SyncField[], object: ZohoSyncObject): SyncField[] {
  return fields.filter((field) => field.object === object);
}

export function objectsWithFields(fields: readonly SyncField[]): ZohoSyncObject[] {
  return ZOHO_SYNC_OBJECTS.filter((object) => fields.some((field) => field.object === object));
}

export type MappingProblem = "name" | "duplicate";

/** Per field: a name Zoho cannot have, or a Zoho field already used by another field of the same object. */
export function problemsOf(draft: MappingDraft, fields: readonly SyncField[]): Record<string, MappingProblem> {
  const found: Record<string, MappingProblem> = {};
  const used = new Map<string, string>();
  for (const field of fields) {
    const value = (draft[field.local] ?? "").trim();
    if (!value) continue;
    if (!NAME_PATTERN.test(value)) {
      found[field.local] = "name";
      continue;
    }
    const key = `${field.object}:${value.toLowerCase()}`;
    if (used.has(key)) found[field.local] = "duplicate";
    else used.set(key, field.local);
  }
  return found;
}

/** Rows to send, in the catalogue's order; blank fields are left out. */
export function mappingOf(draft: MappingDraft, fields: readonly SyncField[]): MappingRow[] {
  return fields.flatMap((field) => {
    const value = (draft[field.local] ?? "").trim();
    return value ? [{ local: field.local, zoho: value }] : [];
  });
}

export function sameDraft(a: MappingDraft, b: MappingDraft, fields: readonly SyncField[]): boolean {
  return fields.every((field) => (a[field.local] ?? "").trim() === (b[field.local] ?? "").trim());
}

export interface RunTotals {
  here: number;
  there: number;
  failed: number;
}

/** The last run in three numbers: written in Sagot, sent to Zoho, not accepted by Zoho. */
export function totalsOf(counts: Readonly<Record<string, Readonly<Record<string, number>>>>): RunTotals {
  const total: RunTotals = { here: 0, there: 0, failed: 0 };
  for (const object of Object.values(counts)) {
    for (const key of HERE_COUNTS) total.here += object[key] ?? 0;
    for (const key of THERE_COUNTS) total.there += object[key] ?? 0;
    total.failed += object[FAILED_KEY] ?? 0;
  }
  return total;
}

const KNOWN_CODES: readonly string[] = ["not_connected", "needs_reconnect", "scope", "unavailable", "busy", "failed"];

/** A stop reason we have words for; anything else falls back to the generic one. */
export function runCodeOf(code: string | null | undefined): ZohoSyncErrorCode | null {
  if (!code) return null;
  return (KNOWN_CODES.includes(code) ? code : "failed") as ZohoSyncErrorCode;
}

export function canSwitchOn(sync: ZohoSync): boolean {
  return sync.connection === "connected";
}
